import { Dispatch, createAction } from '@reduxjs/toolkit';

import type { Event, UnitEventDetails } from '@appTypes/esb';
import { pendingItemsModuleNamespace } from '@modules/advancement/common/constants';
import {
  AdvancementsTabsNames,
  type AdvancementsTabsType,
} from '@modules/advancement/pendingItems/constants';
import locationHash from '@shared/utils/locationHash';

import {
  RosterTabsType,
  advancementModuleName,
  advancementTabNames,
  advancementTypes,
  calendarModuleName,
  messagingModuleName,
  userModuleName,
  youthProfileModuleName,
} from '../constants';

const ROUTE_ADVANCEMENT = `${advancementModuleName}/ROUTE_ADVANCEMENT`;

export const ROUTE_YOUTH_ADVANCEMENTS_LIST = `${youthProfileModuleName}/ROUTE_YOUTH_ADVANCEMENTS_LIST`;
export const goToYouthAdvancementsList = ({
  userId,
  advancementType,
  filter,
}: {
  userId: number;
  advancementType: string;
  filter?: string;
}) => ({
  type: ROUTE_YOUTH_ADVANCEMENTS_LIST,
  payload: {
    userId,
    advancementType,
  },
  query: {
    filter,
  },
});

export const ROUTE_CALENDAR_EVENT_DETAILS = `${calendarModuleName}/ROUTE_CALENDAR_EVENT_DETAILS`;
export const goToCalendarEventDetails = createAction<{
  event?: Event | UnitEventDetails;
  id: number;
}>(ROUTE_CALENDAR_EVENT_DETAILS);

export const goToYouthProfile = createAction(
  `${youthProfileModuleName}/ROUTE_YOUTH_PROFILE`,
  function prepare(userId: number) {
    return {
      payload: {
        userId,
      },
    };
  },
);
export const ROUTE_YOUTH_PROFILE = goToYouthProfile.type;

/**.
 * Navigates to the youth activity logs page i.e. /youthProfile/:userId/activityLogs/:activityType
 **/
export const goToYouthActivityLogs = createAction<{
  userId: number | string;
  /** ActivityType ID */
  activityType: number;
}>(`${youthProfileModuleName}/ROUTE_YOUTH_ACTIVITY_LOGS`);

export const authTokenExpired = createAction<{
  token: string;
}>(`${userModuleName}/AUTH_TOKEN_EXPIRED`);

export const noopAction = createAction('@@noop');

export const setActiveAdvancementsTab = createAction<AdvancementsTabsType>(
  `${pendingItemsModuleNamespace}/SET_ACTIVE_ADVANCEMENTS_TAB`,
);

/**
 * Navigates to the roster route.
 *
 * Usage:
 * ```js
  // goes to the roster by default
  goToRosterRoute()
  // e.g. go to the advancements tab and activate "To Approve"
  goToRosterRoute({
    activeTab: advancementTabNames.ADVANCEMENTS,
    advancementsTab: AdvancementsTabsNames.TO_APPROVE,
  });
  ````
 */
export const goToRosterRoute =
  (
    params: {
      activeTab?: RosterTabsType;
      advancementsTab?: AdvancementsTabsType;
    } = {
      activeTab: advancementTabNames.ROSTER,
    },
  ) =>
  (dispatch: Dispatch) => {
    // Pre-select section Advancements tab
    if (params.advancementsTab) {
      dispatch(setActiveAdvancementsTab(params.advancementsTab));
    }
    const tab = params.activeTab;
    dispatch({ type: ROUTE_ADVANCEMENT, payload: { hash: tab } });
    locationHash.set(tab);
  };

/** Goes to Roster > Advancements > To Approve */
export const goToAdvancementsToApprove = () =>
  goToRosterRoute({
    activeTab: advancementTabNames.ADVANCEMENTS,
    advancementsTab: AdvancementsTabsNames.TO_APPROVE,
  });

/**
 * Navigates to the youth advancement requirements page
 * i.e. /youthProfile/:userId/:advancementType/:advancementId
 *
 * @param userId - The youth member's user ID
 * @param advancementType - The type of advancement (e.g. 'meritBadges', 'ranks', 'awards')
 * @param advancementId - The specific advancement ID
 *
 * @example
 * ```ts
 * dispatch(goToAdvancementDetails({
 *   userId: 12345,
 *   advancementType: 'meritBadges',
 *   advancementId: 67,
 * }));
 * ```
 */
export const goToAdvancementDetails = createAction<
  {
    userId: number;
    advancementType: `${advancementTypes}`;
    advancementId: number;
  },
  'youthProfile/ROUTE_YOUTH_ADV_REQUIREMENTS'
>(`${youthProfileModuleName}/ROUTE_YOUTH_ADV_REQUIREMENTS`);

export const ROUTE_YOUTH_ADV_REQUIREMENTS = goToAdvancementDetails.type;

export const ROUTE_BALANCE_MESSAGING = `${messagingModuleName}/ROUTE_BALANCE_MESSAGING`;

export const routeBalanceMessaging = createAction(
  `${messagingModuleName}/ROUTE_BALANCE_MESSAGING`,
);

export const ROUTE_MBC_MESSAGING = `${messagingModuleName}/ROUTE_MBC_MESSAGING`;

export const routeMbcMessaging = createAction(
  `${messagingModuleName}/ROUTE_MBC_MESSAGING`,
);
